import { useEffect, useMemo } from 'react';
import { useResourcesWithPII } from '@getvim/internal-vim-os-sdk/react';
import { EhrStateWithPII } from '@getvim/internal-vim-os-sdk/types';
import { AppPatient, PatientSourceVaultToken } from '../types';
import { analyticsManager } from '../utils/analyticManager';
import { getPatientInsurance } from '../utils/insurer.utils';
import { eligibilityLogger } from '../utils/logger';

type EhrPatient = NonNullable<EhrStateWithPII['patient']>;

export const convertContentSupplierIdentifiers = (
  ehrPatient: EhrPatient,
): PatientSourceVaultToken[] => {
  if (!ehrPatient.contentSupplierIdentifiers) {
    eligibilityLogger.warning(
      'No contentSupplierIdentifiers on patient — vault token resolution skipped',
      {
        noPHI: true,
        patientToken: ehrPatient.token,
      },
    );
    return [];
  }

  return Object.entries(ehrPatient.contentSupplierIdentifiers).map(
    ([source, contentSupplierValue]) => ({
      source,
      sourceVaultToken: contentSupplierValue.contentSupplierId,
      isInsurer: ehrPatient.insurance?.insurer === source,
    }),
  );
};

export function useAppPatient(): AppPatient | undefined {
  const {
    ehrState: { patient: ehrPatient },
  } = useResourcesWithPII();

  const patient = useMemo(() => {
    if (!ehrPatient) {
      return undefined;
    }

    const patientSourceVaultTokens = convertContentSupplierIdentifiers(ehrPatient);
    const patientInsurance = getPatientInsurance(ehrPatient.insurance, patientSourceVaultTokens);

    return {
      ...ehrPatient,
      patientSourceVaultTokens,
      insurance: patientInsurance,
    };
  }, [ehrPatient]);

  useEffect(() => {
    if (!patient) {
      eligibilityLogger.info('Reset AppPatient - no ehrPatient in context', {
        noPHI: true,
      });
      return;
    }

    eligibilityLogger.info('Initialize AppPatient with patientSourceVaultTokens', {
      noPHI: true,
      patientToken: patient.token,
    });
    analyticsManager.setPatientBaseProperties({
      vimPatientId: patient.identifiers.vimPatientId,
      ehrInsurance: patient.insurance?.ehrInsurance,
      insurer: patient.insurance?.insurer,
    });
  }, [patient]);

  return patient;
}
